import { Client } from 'typesense';

/** User-visible message when VITE Typesense env vars are missing (Beer + Breweries). */
export const TYPESENSE_SEARCH_CONFIG_ERROR =
  'Search is unavailable: Typesense is not configured for this environment.';

export const getTypesenseConfig = () => {
  const rawHost = import.meta.env.VITE_TYPESENSE_HOST;
  const rawApiKey = import.meta.env.VITE_TYPESENSE_SEARCH_API_KEY;
  const trimmedHost =
    typeof rawHost === 'string' ? rawHost.trim() : '';
  const trimmedApiKey =
    typeof rawApiKey === 'string' ? rawApiKey.trim() : '';
  return {
    host: trimmedHost,
    apiKey: trimmedApiKey,
  };
};

/**
 * @returns {string|null} Error message for UI if search cannot run, or null if host and API key exist.
 */
export function getTypesenseSearchConfigError() {
  const { host, apiKey } = getTypesenseConfig();
  if (!host || !apiKey) {
    return TYPESENSE_SEARCH_CONFIG_ERROR;
  }
  return null;
}

/**
 * Returns Typesense collection names for the given environment.
 * @param {string} environment - One of 'development', 'staging', or 'production'.
 * @returns {{breweries: string, beer: string}} Mapping of collection names for the environment.
 */
export const getCollectionNames = (environment) => {
  const devCollections = {
    breweries: 'dev_breweries',
    beer: 'dev_beer',
  };
  const stagingCollections = {
    breweries: 'staging_breweries',
    beer: 'staging_beer',
  };
  const prodCollections = {
    breweries: 'prod_breweries',
    beer: 'prod_beer',
  };
  if (environment === 'development') {
    return devCollections;
  }
  if (environment === 'staging') {
    return stagingCollections;
  }
  if (environment === 'production') {
    return prodCollections;
  }

  // Default to dev collections to avoid unintended writes/reads in production.
  return devCollections;
};

/**
 * Map a Typesense beer hit to BeerCard `highlight` shape (HTML snippet with mark tags).
 * @param {object} hit - Raw Typesense search hit
 * @returns {{name: string}|null}
 */
export function getBeerNameHighlightFromTypesenseHit(hit) {
  const fromHighlight = hit?.highlight?.name?.snippet;
  if (typeof fromHighlight === 'string' && fromHighlight.length > 0) {
    return { name: fromHighlight };
  }
  const highlights = hit?.highlights;
  if (Array.isArray(highlights)) {
    const nameEntry = highlights.find((item) => item.field === 'name');
    const snippet = nameEntry?.snippet;
    if (typeof snippet === 'string' && snippet.length > 0) {
      return { name: snippet };
    }
  }
  return null;
}

/**
 * Creates a Typesense client instance. Use search-only API key for browser.
 * @returns {import('typesense').Client|null} Typesense client or null if config missing
 */
export function createTypesenseClient() {
  const { host, apiKey } = getTypesenseConfig();
  if (!host || !apiKey) {
    return null;
  }
  const url = host.startsWith('http') ? host : `https://${host}`;
  return new Client({
    nodes: [{ url }],
    apiKey,
    connectionTimeoutSeconds: 5,
  });
}

export default getCollectionNames;
